<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { DialogTitle } from '@headlessui/vue'
import { CheckIcon, ExclamationIcon } from '@heroicons/vue/outline'
import { DialogStore, useDialogState } from '../../utilities/useDialog'
import { storeToRefs } from 'pinia'
import BaseButtonPrimary from '../base/BaseButtonPrimary.vue'
import BaseButtonMuted from '../base/BaseButtonMuted.vue'
import Modal from './Modal.vue'

const state = useDialogState() as DialogStore

const { showDialog: open } = storeToRefs(useDialogState())

const confirmButtonRef = ref(null)

// Workaround bc :initial-focus="confirmButtonRef" does only work for native input / button elements
watch(
    () => state.showDialog,
    () => {
        if (state.showDialog) {
            nextTick(() => {
                confirmButtonRef.value.$el.focus()
            })
        }
    }
)
</script>

<template>
    <Modal v-model="open" size="sm" :fullscreen="state.options.fullscreen">
        <div>
            <div
                v-if="state.options.type === 'Success'"
                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckIcon v-if="state.options.type === 'Success'" class="h-6 w-6 text-green-600" aria-hidden="true" />
            </div>
            <div
                v-else-if="state.options.type === 'Warning'"
                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
                <span class="text-center text-xl text-orange-600">!</span>
                <!--                                <ExclamationIcon class="h-6 w-6 text-orange-600" aria-hidden="true" />-->
            </div>
            <div
                v-else-if="state.options.type === 'Error'"
                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <!--                                <span class="text-center text-xl text-red-600">!</span>-->
                <ExclamationIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <div class="mt-3 text-center sm:mt-5">
                <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900">
                    {{ state.title }}
                </DialogTitle>
                <div class="mt-2">
                    <p class="text-sm text-gray-500" v-html="state.message"></p>
                </div>
            </div>
            <iframe
                v-if="state.options.iframeUrl"
                :src="state.options.iframeUrl"
                class="h-[calc(80vh-60px)] w-full"
                frameborder="0">
            </iframe>
            <p v-if="state.options.iframeUrl" class="mx-auto max-w-2xl pt-3 text-xs text-gray-500">
                {{ $t('dialog.iframe_link_notice') }}
                <a class="font-semibold text-primary" target="_blank" :href="state.options.iframeUrl"
                    >{{ $t('dialog.iframe_link_notice_link') }}
                </a>
            </p>
        </div>
        <div
            class="mt-5 sm:mt-6"
            :class="{
                'sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 ': !state.options.hideCancel,
            }">
            <BaseButtonPrimary ref="confirmButtonRef" class="w-full sm:col-start-2" @click="state.confirm()">
                {{ state.options.confirmText }}
            </BaseButtonPrimary>
            <BaseButtonMuted
                v-if="!state.options.hideCancel"
                ref="cancelButtonRef"
                class="mt-3 w-full sm:col-start-1 sm:mt-0"
                @click="state.cancel()">
                {{ state.options.cancelText }}
            </BaseButtonMuted>
        </div>
    </Modal>
</template>
